import React from 'react';
import PropTypes from 'prop-types';
import _path from 'ramda/src/path';
import resx from '../../utilities/resx';
import ListingPrice from '../Common/ListingPrice';
import reMLSData from '../Common/MlsList';
import ListingStreetAddress from '../Common/ListingStreetAddress';

import { RESX_LISTINGS, RESX_GLOBAL } from '../../Constants';

const FeaturedListingTile = props => {
    const {
        Bathrooms,
        Bedrooms,
        City,
        Country,
        CoopFeeAmount,
        Images,
        IsSold,
        ListingType,
        ListingTransactionType,
        MLSGroupType,
        MarketStatus,
        PropertySize,
        SeoUrl,
        State
    } = props.data;

    const hidePremarketDetails =
        MLSGroupType === 0 &&
        MarketStatus === 1 &&
        !(RESAAS.User2.IsProfessional && !RESAAS.User2.IsPendingProfessional) &&
        !IsSold;
    const hidePremarketDetailsClassName = hidePremarketDetails ? 're-listing-tile-hidepremarketdetails' : '';
    const IsCommercial = ListingType === 1;

    // if the listing is premarket
    let PreMarketLabel;
    let PublicPreMarket = false;
    let RenderPreMarketLabel = null;
    if (MarketStatus === 1) {
        // MLS
        if (MLSGroupType !== 0) {
            // not sold and label exists
            if (!IsSold && reMLSData && reMLSData.read(MLSGroupType) && reMLSData.read(MLSGroupType).string) {
                PreMarketLabel = resx(
                    RESX_LISTINGS,
                    `PREMARKET_LABEL_${reMLSData.read(MLSGroupType).string.toUpperCase()}`
                );
            }
            // Public
        } else {
            PublicPreMarket = true;
            PreMarketLabel = resx(RESX_LISTINGS, 'PUBLIC_PRE_MARKET_LISTING_LABEL');
        }
    }
    if (PreMarketLabel) {
        RenderPreMarketLabel = (
            <span
                className={`re-post-label ${PublicPreMarket ? 're-post-label-premarket' : 'mls-premarket-featured'}`}
                data-testid="premarketLabel"
            >
                <span className={`${PublicPreMarket ? '' : 're-icon re-icon-xs re-icon-lock'}`}>{PreMarketLabel}</span>
            </span>
        );
    }

    // if the listing is sold
    let RenderSoldLabel = null;
    if (IsSold) {
        let RenderSoldText = '';
        if (ListingTransactionType) {
            RenderSoldText = resx(RESX_LISTINGS, 'LEASED_TEXT');
        } else {
            RenderSoldText = resx(RESX_LISTINGS, 'SOLD_TEXT');
        }
        RenderSoldLabel = (
            <span className="re-post-label listing-sold" data-testid="soldLabel">
                <span>{RenderSoldText}</span>
            </span>
        );
    }

    const RenderListingDetails = () => {
        let RenderPreMarketDetails = null;
        if (!hidePremarketDetails) {
            RenderPreMarketDetails = (
                <React.Fragment>
                    <span className="price">
                        <ListingPrice listing={props.data} />
                    </span>
                    <span className="re-listing-address">
                        <ListingStreetAddress listing={props.data} />
                    </span>
                </React.Fragment>
            );
        }
        return (
            <h2>
                {RenderPreMarketDetails}
                {City}, {State}, <span className="listing-country">{Country}</span>
            </h2>
        );
    };

    const RenderBedAndBath = () => {
        if (!IsCommercial) {
            return (
                <div className="bedandbath" data-testid="bedandbath">
                    {Bedrooms !== 0 && (
                        <span className="bedrooms">
                            <strong>{Bedrooms}</strong> {resx(RESX_GLOBAL, 'BED_TEXT')}{' '}
                        </span>
                    )}
                    {Bedrooms !== 0 && Bathrooms !== 0 && <span className="slash">|</span>}
                    {Bathrooms !== 0 && (
                        <span className="bathrooms">
                            <strong>{Bathrooms}</strong> {resx(RESX_GLOBAL, 'BATH_TEXT')}
                        </span>
                    )}
                </div>
            );
        }
        return (
            <div className="bedandbath" data-testid="bedandbath">
                {PropertySize !== 0 &&
                    PropertySize !== null && (
                        <span className="totalsqf">
                            <strong>{PropertySize}</strong> {resx(RESX_LISTINGS, 'SQFT')}{' '}
                        </span>
                    )}
                {PropertySize !== 0 &&
                    PropertySize !== null &&
                    CoopFeeAmount !== null && <span className="slash">|</span>}
                {CoopFeeAmount !== null && (
                    <span className="coopfee">
                        <strong>{CoopFeeAmount}%</strong> {resx(RESX_LISTINGS, 'COOP_FEE')}
                    </span>
                )}
            </div>
        );
    };

    // fallback if no image was provided
    let bgImg = null;
    let bgImgStyle = '';
    if (Images && Images.length > 0 && Images[0].Url) {
        bgImg = Images[0].Url;
    }

    const fallbackImg =
        ListingType === 1
            ? 'https://content-eecuhxg6gch7hahp.a03.azurefd.net/assets/db065b82-052d-417a-bdb2-e5ade27371d0.png'
            : 'https://content-eecuhxg6gch7hahp.a03.azurefd.net/assets/4788bef9-098d-41c4-9705-e57d5663635f.png';

    if (bgImg) {
        bgImgStyle = `url(${Images[0].Url}) center center, #bdbdbd url(${fallbackImg}) center center`;
    } else {
        bgImgStyle = `#bdbdbd url(${fallbackImg}) center center`;
    }

    return (
        <li className="listing-tile">
            <a href={SeoUrl} className="seo-link" data-link={SeoUrl}>
                <div
                    className={`listing-tile-images ${hidePremarketDetailsClassName}`}
                    data-listingtype={ListingType}
                    style={{ background: `${bgImgStyle}` }}
                    data-testid="background-container"
                />
            </a>
            <div className="details re-container-md">
                {RenderPreMarketLabel}
                {RenderSoldLabel}
                <div className="text" data-testid="listingDetails">
                    {RenderListingDetails()}
                </div>
                {RenderBedAndBath()}
            </div>
        </li>
    );
};

FeaturedListingTile.propTypes = {
    data: PropTypes.object.isRequired
};

export default FeaturedListingTile;
